import {useRef, forwardRef, useImperativeHandle} from 'react'
import {Box} from '@mui/material'
import {AgGridReact} from 'ag-grid-react'
import type {AgGridReact as AgGridReactType} from 'ag-grid-react'
import type {IFilterParams, IDoesFilterPassParams} from 'ag-grid-community'
import {formatEffectiveDate, formatFullTimestamp} from '../../utils/dateFormatter'
import {Version} from './types'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-balham.css'

class StateDropdownFilter {
    filterParams!: IFilterParams
    gui!: HTMLDivElement
    selectedValue: string = ''
    eSelect!: HTMLSelectElement

    init(params: IFilterParams) {
        this.filterParams = params
        this.gui = document.createElement('div')
        this.gui.style.padding = '8px'
        this.gui.style.minWidth = '120px'

        this.eSelect = document.createElement('select')
        this.eSelect.style.width = '100%'
        this.eSelect.style.padding = '6px 8px'
        this.eSelect.style.borderRadius = '4px'
        this.eSelect.style.border = '1px solid #d0d0d0'
        this.eSelect.style.fontSize = '13px'

        // Get unique states
        const states = new Set<string>()
        params.api.forEachNode((node) => {
            if (node.data?.state) {
                states.add(node.data.state)
            }
        })
        const uniqueStates = Array.from(states).sort()

        // Add "All" option
        const allOption = document.createElement('option')
        allOption.value = ''
        allOption.text = 'All'
        this.eSelect.appendChild(allOption)

        // Add state options
        uniqueStates.forEach((state) => {
            const option = document.createElement('option')
            option.value = state
            option.text = state
            this.eSelect.appendChild(option)
        })

        this.eSelect.addEventListener('change', () => {
            this.selectedValue = this.eSelect.value
            this.filterParams.filterChangedCallback()
        })

        this.gui.appendChild(this.eSelect)
    }

    getGui() {
        return this.gui
    }

    isFilterActive() {
        return this.selectedValue !== ''
    }

    doesFilterPass(params: IDoesFilterPassParams) {
        if (!this.selectedValue) return true
        return params.data.state === this.selectedValue
    }

    getModel() {
        return this.selectedValue ? {value: this.selectedValue} : null
    }

    setModel(model: any) {
        this.selectedValue = model?.value || ''
        if (this.eSelect) {
            this.eSelect.value = this.selectedValue
        }
    }
}

interface VersionsTableProps {
    versions: Version[]
    loading: boolean
    searchText: string
    showDeleted: boolean
    onRowClick: (version: Version) => void
}

export interface VersionsTableRef {
    onFilterChanged: () => void
}

const GRID_STATE_KEY = 'versionsTable_gridState'

const VersionsTable = forwardRef<VersionsTableRef, VersionsTableProps>(({
    versions,
    loading,
    searchText,
    onRowClick
}, ref) => {
    const gridRef = useRef<AgGridReactType>(null)

    useImperativeHandle(ref, () => ({
        onFilterChanged: () => gridRef.current?.api?.onFilterChanged()
    }))

    // Restore grid state when grid is ready
    const handleGridReady = () => {
        const savedState = localStorage.getItem(GRID_STATE_KEY)
        if (savedState && gridRef.current?.api) {
            try {
                const state = JSON.parse(savedState)
                
                // Restore sort state first
                if (state.sortModel && state.sortModel.length > 0) {
                    gridRef.current.api.applyColumnState({
                        state: state.sortModel,
                        defaultState: {sort: null}
                    })
                }
                
                // Then restore filter state
                if (state.filterModel) {
                    gridRef.current.api.setFilterModel(state.filterModel)
                }
            } catch (e) {
                console.error('Failed to restore grid state:', e)
            }
        }
    }

    // Save filter state to localStorage
    const handleFilterChanged = () => {
        if (gridRef.current?.api) {
            const filterModel = gridRef.current.api.getFilterModel()
            const sortModel = gridRef.current.api.getColumnState().filter(col => col.sort != null)
            const state = {
                filterModel,
                sortModel
            }
            localStorage.setItem(GRID_STATE_KEY, JSON.stringify(state))
        }
    }

    // Save sort state to localStorage
    const handleSortChanged = () => {
        if (gridRef.current?.api) {
            const filterModel = gridRef.current.api.getFilterModel()
            const sortModel = gridRef.current.api.getColumnState().filter(col => col.sort != null)
            const state = {
                filterModel,
                sortModel
            }
            localStorage.setItem(GRID_STATE_KEY, JSON.stringify(state))
        }
    }

    const isExternalFilterPresent = () => {
        return searchText !== ''
    }

    const doesExternalFilterPass = (node: any) => {
        // If no search text, pass the filter
        if (!searchText) return true

        const data = node.data
        const searchLower = searchText.toLowerCase()

        const fieldsToSearch = [
            data.versionId,
            data.state,
            data.clientName,
            data.policySetName,
            data.carrierName,
            data.label,
            data.effectiveDate,
            data.publishTimestamp,
            ...(data.versionKeys || [])
        ]

        return fieldsToSearch.some(field =>
            field && String(field).toLowerCase().includes(searchLower)
        )
    }

    const VersionCellRenderer = (params: any) => {
        const displayValue = params.data?.label || params.data?.versionId
        return (
            <span
                onClick={() => onRowClick(params.data)}
                style={{
                    color: '#1976d2',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                }}
            >
                {displayValue}
            </span>
        )
    }

    const versionComparator = (versionA: string, versionB: string) => {
        if (!versionA || !versionB) {
            return versionA ? 1 : versionB ? -1 : 0
        }

        const partsA = versionA.split('.').map(Number)
        const partsB = versionB.split('.').map(Number)
        const maxLength = Math.max(partsA.length, partsB.length)

        for (let i = 0; i < maxLength; i++) {
            const a = partsA[i] || 0
            const b = partsB[i] || 0
            if (a !== b) {
                return a - b
            }
        }

        return 0
    }

    const columnDefs = [
        {
            field: 'versionId',
            headerName: 'Version',
            flex: 2,
            minWidth: 100,
            maxWidth: 120,
            cellRenderer: VersionCellRenderer,
            comparator: (_valueA: string, _valueB: string, nodeA: any, nodeB: any) => {
                const displayA = nodeA.data?.label || nodeA.data?.versionId
                const displayB = nodeB.data?.label || nodeB.data?.versionId
                return versionComparator(displayA, displayB)
            }
        },
        // {
        //     field: 'versionKeys',
        //     headerName: 'VK Cnt',
        //     flex: 0.6,
        //     minWidth: 80,
        //     maxWidth: 100,
        //     valueFormatter: (params: any) => {
        //         return params.data?.versionKeys?.length || 0
        //     }
        // },
        {
            field: 'state',
            headerName: 'State',
            flex: 0.5,
            minWidth: 80,
            maxWidth: 100,
            filter: StateDropdownFilter
        },
        {field: 'policySetName', headerName: 'Rating Program', flex: 1.5, minWidth: 150},
        {
            field: 'carrierName',
            headerName: 'Carrier',
            flex: 0.8,
            minWidth: 100
        },
        {
            field: 'effectiveDate',
            headerName: 'Effective Date',
            flex: 1,
            minWidth: 120,
            valueFormatter: (params: any) => {
                return formatEffectiveDate(params.data?.effectiveDate)
            },
            filter: 'agDateColumnFilter'
        },
        {
            field: 'publishTimestamp',
            headerName: 'Published',
            flex: 1.2,
            minWidth: 170,
            valueFormatter: (params: any) => {
                return formatFullTimestamp(params.data?.publishTimestamp)
            },
            filter: 'agDateColumnFilter'
        },
        {
            field: 'versionId',
            headerName: 'Version ID',
            flex: 1,
        }
    ]

    const getRowStyle = (params: any) => {
        if (params.data?.status === 'DELETED') {
            return { backgroundColor: '#ffebee' }
        }
        return undefined
    }

    if (loading) {
        return <div>Loading versions...</div>
    }

    return (
        <Box sx={{height: 'calc(100vh - 200px)', width: '100%'}}>
            <div className="ag-theme-balham" style={{height: '100%', width: '100%'}}>
                <AgGridReact
                    ref={gridRef}
                    rowData={versions}
                    rowHeight={34}
                    enableCellTextSelection={true}
                    ensureDomOrder={true}
                    autoSizeStrategy={{type: 'fitGridWidth', defaultMinWidth: 100}}
                    columnDefs={columnDefs}
                    defaultColDef={{
                        sortable: true,
                        filter: true,
                        resizable: true,
                    }}
                    isExternalFilterPresent={isExternalFilterPresent}
                    doesExternalFilterPass={doesExternalFilterPass}
                    getRowStyle={getRowStyle}
                    pagination={true}
                    onGridReady={handleGridReady}
                    onFilterChanged={handleFilterChanged}
                    onSortChanged={handleSortChanged}
                />
            </div>
        </Box>
    )
})

VersionsTable.displayName = 'VersionsTable'

export default VersionsTable
